import { PATHS } from '@/config/paths';

import type { RoleDashboardConfig, UserRole } from '../types/auth.types';

export const ROLE_DASHBOARD_CONFIG: Record<UserRole, RoleDashboardConfig> = {
  admin: { path: PATHS.DASHBOARD, view: 'admin' },
  editor: { path: PATHS.DASHBOARD, view: 'editor' },
  viewer: { path: PATHS.DASHBOARD, view: 'viewer' },
  customer: { path: PATHS.DASHBOARD, view: 'customer' },
  staff: { path: PATHS.DASHBOARD, view: 'staff' },
  leader: { path: PATHS.DASHBOARD, view: 'editor' },
  researcher: { path: PATHS.DASHBOARD, view: 'editor' },
  operator: { path: PATHS.DASHBOARD, view: 'staff' },
  user: { path: PATHS.DASHBOARD, view: 'viewer' },
} as const;

export function getDashboardConfigByRole(role: UserRole): RoleDashboardConfig {
  return ROLE_DASHBOARD_CONFIG[role] || ROLE_DASHBOARD_CONFIG.viewer;
}

export function getDashboardPathByRole(role: UserRole): string {
  return getDashboardConfigByRole(role).path;
}

export function getDashboardViewByRole(role: UserRole): RoleDashboardConfig['view'] {
  return getDashboardConfigByRole(role).view;
}

// Role-based menu configuration
export interface MenuItemConfig {
  key: string;
  labelKey: string;
  icon?: string;
  requiredRoles?: UserRole[];
  requiredPermissions?: string[];
}

const ALL_APP_ROLES: UserRole[] = [
  'admin',
  'leader',
  'editor',
  'researcher',
  'viewer',
  'customer',
  'staff',
  'operator',
  'user',
];

export const ROLE_MENU_CONFIG: MenuItemConfig[] = [
  {
    key: 'overview',
    labelKey: 'sidebar.nav.overview',
    requiredRoles: ALL_APP_ROLES,
  },
  {
    key: 'alerts',
    labelKey: 'sidebar.nav.alerts',
    requiredRoles: ['admin', 'leader', 'editor', 'researcher', 'staff', 'operator'],
  },
  {
    key: 'analytics',
    labelKey: 'sidebar.nav.analytics',
    requiredRoles: ['admin', 'leader', 'editor', 'researcher'],
  },
  {
    key: 'map-view',
    labelKey: 'sidebar.nav.mapView',
    requiredRoles: ALL_APP_ROLES,
  },
  {
    key: 'reports',
    labelKey: 'sidebar.nav.reports',
    requiredRoles: ['admin', 'leader', 'editor', 'researcher'],
    requiredPermissions: ['reports:view'],
  },
  {
    key: 'users',
    labelKey: 'sidebar.nav.users',
    requiredRoles: ['admin'],
    requiredPermissions: ['admin:users'],
  },
  {
    key: 'settings',
    labelKey: 'sidebar.nav.settings',
    requiredRoles: ['admin'],
    requiredPermissions: ['settings:view'],
  },
];

export function filterMenuItemsByRole(
  menus: MenuItemConfig[],
  userRole: UserRole,
  userPermissions: string[],
): MenuItemConfig[] {
  return menus.filter((menu) => {
    if (menu.requiredRoles && !menu.requiredRoles.includes(userRole)) {
      return false;
    }

    if (userRole !== 'admin' && menu.requiredPermissions?.length) {
      return menu.requiredPermissions.some((perm) => userPermissions.includes(perm));
    }

    return true;
  });
}

// Role display names for UI
export const ROLE_DISPLAY_NAMES: Record<UserRole, string> = {
  admin: 'Administrator',
  leader: 'Leader',
  editor: 'Editor',
  researcher: 'Researcher',
  viewer: 'Viewer',
  customer: 'Customer',
  staff: 'Staff',
  operator: 'Operator',
  user: 'User',
} as const;

// Role badges for UI
export const ROLE_BADGE_COLORS: Record<UserRole, string> = {
  admin: 'red',
  leader: 'magenta',
  editor: 'blue',
  researcher: 'cyan',
  viewer: 'green',
  customer: 'orange',
  staff: 'purple',
  operator: 'geekblue',
  user: 'gold',
} as const;
